import { Component, inject, input, output } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiV1Service, FileAttachmentIntake, FileStatus } from 'api';
import { TranslateModule } from '@ngx-translate/core';
import { ProgressBarComponent } from 'common/common/progress-bar.component';
import { FileTypeLabelComponent } from 'common/files/file-type-label.component';
import { DownloadService } from 'common/files/download.service';
import { FileProperties } from 'common/files/file-properties';
import { ButtonDirective } from 'common/button/button.directive';
import { IconDirective } from 'common/icon/icon.directive';

@Component({
  selector: 'comp-file-status',
  templateUrl: './file-status.component.html',
  styleUrls: ['./file-status.component.scss'],
  imports: [TranslateModule, ProgressBarComponent, FileTypeLabelComponent, ButtonDirective, IconDirective],
})
export class FileStatusComponent {
  private readonly apiService = inject(ApiV1Service);
  private readonly downloadService = inject(DownloadService);

  readonly file = input.required<FileProperties>();
  readonly uploadPercentage = input<number>(0);
  readonly onDelete = output<{ fileAttachmentId: number; file: FileAttachmentIntake }>();
  readonly onReuseConfirmed = output();

  private readonly DOWNLOADABLE_STATUSES = new Set<FileStatus>([
    FileStatus.Clean,
    FileStatus.PendingAnalysis,
    FileStatus.AnalysisComplete,
    FileStatus.AnalysisFailed,
  ]);

  protected download(fileId: number, fileName: string): void {
    this.downloadService.download(fileId, fileName);
  }

  protected fileStatusLabelKey(status: FileStatus): string {
    switch (status) {
      case FileStatus.Uploading:
        return 'requirementIntake.evidence.scan.uploading';
      case FileStatus.PendingScan:
        return 'requirementIntake.evidence.scan.pending';
      case FileStatus.Clean:
        return 'requirementIntake.evidence.scan.clean';
      case FileStatus.Infected:
        return 'requirementIntake.evidence.scan.infected';
      case FileStatus.PendingAnalysis:
        return 'requirementIntake.evidence.scan.pendingAnalysis';
      case FileStatus.AnalysisComplete:
        return 'requirementIntake.evidence.scan.analysisComplete';
      case FileStatus.FailedScan:
        return 'requirementIntake.evidence.scan.failedScan';
      case FileStatus.AnalysisFailed:
        return 'requirementIntake.evidence.scan.analysisFailed';
      default:
        return 'requirementIntake.evidence.scan.pending';
    }
  }

  protected fileStatusClass(status: FileStatus): string {
    return 'file-status file-status-' + status;
  }

  protected isDownloadableStatus(fileStatus: FileStatus): boolean {
    return this.DOWNLOADABLE_STATUSES.has(fileStatus);
  }

  protected isDeletableStatus(fileStatus: FileStatus): boolean {
    return fileStatus !== FileStatus.Uploading;
  }

  protected onDeleteRequested(fileOrUpload: FileProperties) {
    this.onDelete.emit({
      fileAttachmentId: fileOrUpload.id!,
      file: {
        id: fileOrUpload.id!,
        updatedAt: fileOrUpload.updatedAt!,
        filename: fileOrUpload.filename,
        filetype: fileOrUpload.filetype!,
        filesize: fileOrUpload.filesize!,
        fileStatus: fileOrUpload.fileStatus,
        confirmReuse: false,
      },
    });
  }

  protected readonly FileStatus = FileStatus;

  protected async confirmReuse(fileOrUpload: FileProperties) {
    await firstValueFrom(this.apiService.confirmFileAttachmentReuse(fileOrUpload.id!));
    this.onReuseConfirmed.emit();
  }
}
